import { makeAutoObservable, observable } from 'mobx';
import { TreeDataNode, TreeProps } from 'antd';
import { AntTreeRef } from 'controls/react/ant/antTree';
import React from 'react';
import { linkModel, MobxManager } from 'framework/mobx-integration';
import { DeleteOutlined, EditOutlined, HolderOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { EventDataNode, Key } from 'rc-tree/lib/interface';
import { DashboardsListEntry } from 'areas/dashboards/DashboardsGridStore/types';
import { ModalPosition, openModal } from 'controls/react/ant/antModal';
import { Section } from 'controls/react/layout/section';
import { AntInput } from 'controls/react/ant/antInput';
import { observer } from 'mobx-react';
import { Link } from 'controls/react/link';
import { DashboardsRouter } from 'areas/dashboards/bundleDescription';
import { apiFetch } from 'framework/api';
import { updateDashboardPath } from 'areas/dashboards/pages/DashboardsPage/components/DashboardsTree/api';
import { translator } from 'core/localization';
import { DashboardsTreeProps } from 'areas/dashboards/pages/DashboardsPage/components/DashboardsTree/index';
import { DataNode } from 'antd/lib/tree';
import { GroupByMode } from 'controls/grid/types';
import b_ from 'b_';
import { AntButton } from 'controls/react/ant/antButton';
import { AutoReloadPlugin, UpdateMode } from 'controls/grid/plugins/autoReloadPlugin';

export const bDbTree = b_.with('dashboards-tree');

export const i18n = translator({
	'Enable editing': {
		no: 'Aktiver redigering',
	},
	'Rename folder': {
		no: 'Gi nytt navn',
	},
	Dashboards: {
		no: 'Dashboarder',
	},
	Create: {
		no: 'Opprett',
	},
	Edit: {
		no: 'Rediger',
	},
	Delete: {
		no: 'Slett',
	},
	Move: {
		no: 'Flytt',
	},
	Rename: {
		no: 'Gi nytt navn',
	},
});

export class DashboardTreeStore {
	treeComponent: AntTreeRef;
	dashboardsTree: TreeDataNode[] = [];
	mobx = new MobxManager();
	dashboards: DashboardsListEntry[] = [];
	newFolderName: string;
	loading: boolean = false;
	draggableSettings = {};
	expandedKeys: string[] = [];
	recoverers: (() => void)[] = [];
	get notEmpty() {
		return this.dashboardsTree?.[0]?.children?.length > 0;
	}

	get autoReloadPlugin() {
		return this.props.gridStore?.plugins.find((x) => x.id == 'auto-reload') as AutoReloadPlugin<DashboardsListEntry>;
	}
	constructor(public props: DashboardsTreeProps) {
		makeAutoObservable(this);
		this.mobx.reaction(
			() => [this.props.gridStore.dataProvider.data, this.props.gridStore.dataProvider.data?.length],
			() => {
				this.refreshData();
			},
		);
	}

	async init() {
		if (
			this.props.gridStore.state.currentView.groupByMode == GroupByMode.path ||
			this.props.gridStore.state.currentView.groupBy?.length
		) {
			const currentGroupBy = this.props.gridStore.state.currentView.groupBy;
			const currentGroupByMode = this.props.gridStore.state.currentView.groupByMode;
			this.props.gridStore.state.currentView.groupBy = [];
			this.props.gridStore.state.currentView.groupByMode = GroupByMode.columns;
			await this.props.gridStore.remoteDataProvider.silentReload();
			this.recoverers.push(() => {
				this.props.gridStore.state.currentView.groupBy = currentGroupBy;
				this.props.gridStore.state.currentView.groupByMode = currentGroupByMode;
			});
		}
		const autoReloadMode = this.autoReloadPlugin.timerSettings.mode;
		this.autoReloadPlugin.timerSettings.mode = UpdateMode.None;
		this.recoverers.push(() => {
			this.autoReloadPlugin.timerSettings.mode = autoReloadMode;
		});
		this.refreshData();
		this.draggableSettings = {
			nodeDraggable: () => !this.props.settings?.disableDragAndDrop,
			icon: <HolderOutlined className={'dnd-holder'} title={i18n('Move')} />,
		};
	}

	refreshData = () => {
		this.dashboards = [...this.props.gridStore.dataProvider.data.map((x) => x.data)];
		this.buildTree();
	};

	buildTree() {
		this.dashboardsTree = [
			{
				title: i18n('Dashboards'),
				key: '',
				children: [],
				disabled: true,
			},
		];
		this.expand('');

		this.dashboards
			.sort((lhv, rhv) => lhv.tag.localeCompare(rhv.tag))
			.forEach((item) => {
				const targetPath = (item.path?.split('/') ?? []).filter((x) => !!x);
				if (!targetPath.length) {
					//dashboard at root level
					if (!this.props.settings?.hideLeafs) {
						this.dashboardsTree[0].children.push(this.createDashboardNode(item));
						this.dashboardsTree[0].children.sort(this.compareNodes);
					}
				} else {
					this.placeDashboardIntoTargetPath(item, this.dashboardsTree[0].children, '', targetPath);
				}
			});
	}

	expand = (key: string) => {
		if (this.expandedKeys.includes(key)) {
			return;
		}
		this.expandedKeys.push(key);
		this.expandedKeys = [...this.expandedKeys];
	};

	compareNodes = (lhv: TreeDataNode, rhv: TreeDataNode) => {
		const lhvL = lhv.isLeaf ? 1 : 0;
		const rhvL = rhv.isLeaf ? 1 : 0;
		return (rhvL - lhvL) * 10 + lhv.title.toString().localeCompare(rhv.title.toString());
	};

	onExpand = (
		expandedKeys: Key[],
		info: {
			node: EventDataNode<TreeDataNode>;
			expanded: boolean;
			nativeEvent: MouseEvent;
		},
	) => {
		if (info.expanded) {
			this.expandedKeys = [...expandedKeys.map((x) => x.toString()), info.node.key.toString()];
		} else {
			this.expandedKeys = expandedKeys.map((x) => x.toString()).filter((x) => x != info.node.key.toString());
		}
	};

	placeDashboardIntoTargetPath = (
		dashboard: DashboardsListEntry,
		currentLevelNodes: TreeDataNode[],
		currentPath: string,
		targetPath: string[],
	) => {
		const newParentPath = currentPath + '/' + targetPath[0];
		const newLevelParent = this.findOrCreateNodeForCurrentPath(currentLevelNodes, targetPath[0], newParentPath);

		if (targetPath.length == 1) {
			if (!this.props.settings?.hideLeafs) {
				newLevelParent.children.push(this.createDashboardNode(dashboard, newParentPath));
				newLevelParent.children.sort(this.compareNodes);
			}
			return;
		}

		this.placeDashboardIntoTargetPath(dashboard, newLevelParent.children, newParentPath, targetPath.slice(1));
	};

	createDashboardNode = (dashboard: DashboardsListEntry, parentKey: string = '') => {
		return {
			title: dashboard.tag,
			isLeaf: true,
			key: parentKey + '/' + dashboard.id,
		};
	};

	findOrCreateNodeForCurrentPath = (nodes: TreeDataNode[], title: string, key: string) => {
		let targetNode = nodes.find((x) => x.key == key);
		if (!targetNode) {
			targetNode = observable({ children: [], title, key });
			nodes.push(targetNode);
			this.expand(key);
			nodes.sort(this.compareNodes);
		}
		return targetNode;
	};

	onDrop: TreeProps['onDrop'] = async (info) => {
		this.loading = true;
		let node = info.node as TreeDataNode;
		if (node.isLeaf && !info.dropToGap) {
			info.dropToGap = true;
		}
		if (!node.isLeaf && info.dropToGap) {
			const lastNode = this.findLastOfLasts(node);
			node = this.findNode(lastNode.key.toString(), this.dashboardsTree);
			info.dropToGap = !!this.getDashboardByKey(node.key);
		}

		const dropKey = node.key;
		const dragKey = info.dragNode.key;

		const loop = (
			data: TreeDataNode[],
			key: React.Key,
			callback: (node: TreeDataNode, i: number, data: TreeDataNode[]) => void,
		) => {
			for (let i = 0; i < data.length; i++) {
				if (data[i].key === key) {
					return callback(data[i], i, data);
				}
				if (data[i].children) {
					loop(data[i].children, key, callback);
				}
			}
		};
		const data = [...this.dashboardsTree];

		// Find dragObject
		let dragObj: TreeDataNode;
		loop(data, dragKey, (item, index, arr) => {
			arr.splice(index, 1);
			dragObj = item;
		});

		if (!info.dropToGap) {
			// Drop on the content
			loop(data, dropKey, (item) => {
				item.children = item.children || [];
				// where to insert. New item was inserted to the start of the array in this example, but can be anywhere
				item.children.unshift(dragObj);
				item.children.sort(this.compareNodes);
			});
		} else {
			let ar: TreeDataNode[] = [];
			let i: number;
			loop(data, dropKey, (_item, index, arr) => {
				ar = arr;
				i = index;
			});
			ar.splice(i + 1, 0, dragObj);
			ar.sort(this.compareNodes);
		}
		this.dashboardsTree = data;
		await this.updateDashboard(dragObj, node, info.dropToGap);
		this.loading = false;
	};

	findLastOfLasts = (node: TreeDataNode): TreeDataNode => {
		if (!node.children || node.children.length === 0) {
			return node;
		}
		return this.findLastOfLasts(node.children[node.children.length - 1]);
	};

	updateDashboard = async (node: TreeDataNode, destination: TreeDataNode, isNeighbour: boolean) => {
		if (!node.isLeaf) {
			return;
		}
		const item = this.getDashboardByKey(node.key);
		let newKey: string;
		if (isNeighbour) {
			const pathArr = destination.key
				.toString()
				.split('/')
				.filter((x) => !!x);
			pathArr[pathArr.length - 1] = item.id;
			newKey = `/${pathArr.join('/')}`;
		} else if (!destination.isLeaf) {
			newKey = `${destination.key.toString()}/${item.id}`;
		} else {
			newKey = `/${item.id}`;
		}
		item.path = newKey.replace(`/${item.id}`, '');
		node.key = newKey;
		await apiFetch(
			updateDashboardPath({
				dashboardId: item.id,
				path: item.path,
			}),
		);
	};

	getDashboardByKey = (key: Key) => {
		const id = key.toString().split('/').at(-1);
		return this.dashboards.find((x) => x.id == id);
	};

	addNode = (parent: TreeDataNode) => {
		openModal(
			{
				title: i18n('Create'),
				positionType: ModalPosition.Mouse,
				destroyOnClose: true,
				onOk: () => {
					const node = this.findNode(parent.key.toString(), this.dashboardsTree);
					const key = node.key.toString() + '/' + this.newFolderName;
					this.findOrCreateNodeForCurrentPath(node.children, this.newFolderName, key);
					this.dashboardsTree = [...this.dashboardsTree];
					this.newFolderName = '';
				},
				onCancel: () => {
					this.newFolderName = '';
				},
			},
			<FolderNameEditor model={this} />,
		);
	};

	findNode = (key: string, nodes: TreeDataNode[]): TreeDataNode => {
		const node = nodes.find((x) => x.key.toString() == key);
		if (node) {
			return node;
		}
		for (const n of nodes) {
			const childNode = this.findNode(key, n.children ?? []);
			if (childNode) {
				return childNode;
			}
		}
	};

	editNode = (node: TreeDataNode) => {
		this.newFolderName = node.title?.toString();
		openModal(
			{
				title: i18n('Rename folder'),
				positionType: ModalPosition.Mouse,
				destroyOnClose: true,
				onOk: async () => {
					await this.updateDashboardsPath(node.key?.toString(), this.newFolderName);
					this.newFolderName = '';
				},
				onCancel: () => {
					this.newFolderName = '';
				},
				okText: i18n('Rename'),
			},
			<FolderNameEditor model={this} />,
		);
	};
	deleteNode = async (key: string) => {
		await this.updateDashboardsPath(key);
	};

	updateDashboardsPath = async (key: string, newName?: string) => {
		const dashboards = this.dashboards.filter((x) => x.path?.startsWith(key));
		const keysArray = key.split('/');
		keysArray.pop();
		if (newName) {
			keysArray.push(newName);
		}
		const newSubKey = keysArray.join('/');
		await Promise.all(
			dashboards.map(async (db) => {
				db.path = db.path.replace(key, newSubKey);
				await apiFetch(
					updateDashboardPath({
						dashboardId: db.id,
						path: db.path,
					}),
				);
			}),
		);
		this.buildTree();
	};

	titleRender = (node: TreeDataNode) => {
		if (node.isLeaf) {
			const item = this.getDashboardByKey(node.key);
			if (item.uiContext === 'MOBILE') {
				return (
					<a className="cw_grid_link" data-id="${item.id}">
						{item.tag}
					</a>
				);
			} else {
				return <Link url={DashboardsRouter.viewer(item.id)}>{item.tag}</Link>;
			}
		}
		return (
			<span className={bDbTree('node')}>
				<label>{node.title.toString()}</label>
				<AntButton
					size={'small'}
					type={'text'}
					icon={<PlusCircleOutlined />}
					title={i18n('Create')}
					onClick={() => this.addNode(node)}
				/>
				{node.key != '' && !this.props.settings?.disableDelete && (
					<>
						<AntButton
							size={'small'}
							type={'text'}
							icon={<EditOutlined />}
							onClick={() => this.editNode(node)}
							title={i18n('Edit')}
						/>
						<AntButton
							size={'small'}
							type={'text'}
							icon={<DeleteOutlined />}
							onClick={() => this.deleteNode(node.key.toString())}
							title={i18n('Delete')}
						/>
					</>
				)}
			</span>
		);
	};

	onFolderSelected = (e: React.MouseEvent<HTMLSpanElement>, eventData: EventDataNode<DataNode>) => {
		this.props.onSelect?.(eventData.key?.toString());
	};

	destroy = () => {
		this.recoverers.forEach((recoverer) => recoverer());
		this.mobx.destroy();
	};
}

const FolderNameEditor = observer(({ model }: { model: { newFolderName: string } }) => (
	<Section appearance={'none'} contentPadding={true}>
		<AntInput {...linkModel(model, 'newFolderName')} />
	</Section>
));
